import React, { useState } from "react";
import { EuiFlexItem, EuiTitle, EuiText } from "@elastic/eui";

interface HoverImageCardProps {
  imageUrl: string;
  alt: string;
  title: string;
  description: string;
  maxHeight?: string;
}

// Not clickable -> the caption is always visible instead of being revealed on
// hover (a hover effect would imply the image is a link).
export const HoverImageCard: React.FC<HoverImageCardProps> = ({
  imageUrl,
  alt,
  title,
  description,
  maxHeight = "450px",
}) => (
  <EuiFlexItem grow={false}>
    <div
      style={{
        position: "relative",
        borderRadius: "8px",
        overflow: "hidden",
        maxHeight: maxHeight,
        width: "auto",
      }}
    >
      <img
        src={imageUrl}
        alt={alt}
        style={{
          borderRadius: "8px",
          width: "100%",
          maxHeight: maxHeight,
          objectFit: "cover",
          display: "block",
        }}
      />
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "48px 24px 20px",
          background: "linear-gradient(to top, var(--ak-overlay) 0%, transparent 100%)",
          textAlign: "center",
        }}
      >
        <EuiTitle size="s">
          <h2
            style={{
              color: "var(--ak-text-on-accent)",
              textShadow: "0 2px 8px var(--ak-overlay)",
              margin: 0,
            }}
          >
            {title}
          </h2>
        </EuiTitle>
        <EuiText size="s" style={{ color: "var(--ak-text-on-accent)", marginTop: 4 }}>
          <p style={{ margin: 0, textShadow: "0 2px 8px var(--ak-overlay)" }}>{description}</p>
        </EuiText>
      </div>
    </div>
  </EuiFlexItem>
);

export const HoverImageCardSlideUp: React.FC<HoverImageCardProps> = ({
  imageUrl,
  alt,
  title,
  description,
  maxHeight = "350px",
}) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <EuiFlexItem grow={false}>
      <div
        style={{
          position: "relative",
          borderRadius: "8px",
          overflow: "hidden",
          cursor: "pointer",
          maxHeight: maxHeight,
          width: "auto",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Image */}
        <img
          src={imageUrl}
          alt={alt}
          style={{
            borderRadius: "8px",
            width: "100%",
            maxHeight: maxHeight,
            objectFit: "cover",
            display: "block",
            transition: "transform 0.4s ease",
            transform: isHovered ? "scale(1.08)" : "scale(1)",
          }}
        />

        {/* Dark overlay on hover */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            opacity: isHovered ? 1 : 0,
            transition: "opacity 0.4s ease",
          }}
        />

        {/* Text Content slides from bottom */}
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: isHovered ? "0" : "-100%",
            padding: "30px",
            background:
              "linear-gradient(180deg, var(--ak-accent) 0%, var(--ak-accent) 100%)",
            transition: "bottom 0.4s ease",
          }}
        >
          <EuiTitle size="m">
            <h3 style={{ color: "var(--ak-text-on-accent)", margin: 0, marginBottom: "12px" }}>
              {title}
            </h3>
          </EuiTitle>
          <EuiText size="s" style={{ color: "var(--ak-text-on-accent)" }}>
            <p style={{ margin: 0 }}>{description}</p>
          </EuiText>
        </div>
      </div>
    </EuiFlexItem>
  );
};
